'use client';
import Header from '@/components/client/Header';
import DynamicBody from '@/components/core/DynamicBody';
import { AppSupport, Footer } from '@/app/_components/platform';
import { TopBanner, FaqBanner, Faqs, HighlightedCTA } from '@/app/components/shared';

import { ClientProvider } from '@/contexts/client.context';

import { products } from '@/utils/products';
import DynamicHeader from '@/app/components/platform/DynamicHeader';
import { mergeObjects } from '@/utils/merge-objects';
import usePersonalization from '@/utils/usePersonalization';

import { getEligibilityClaim } from '@/utils/eligibility-claim';

function getValue(value, client) {
  let finalValue = null;

  if (!!client?.heroHeader.length && client?.heroHeader[0]?.content[value]) {
    finalValue = client?.heroHeader[0]?.content[value];
  } else if (client?.header && client?.header[value]) {
    finalValue = client?.header[value];
  } else if (client?.template?.data?.attributes?.heroHeader[0]?.content[value]) {
    finalValue = client.template?.data?.attributes?.heroHeader[0]?.content[value];
  } else if (client?.template?.data?.attributes?.header[value]) {
    finalValue = client.template?.data?.attributes?.header[value];
  }

  return finalValue;
}

const ClientPage = ({ client, personalization, referral = false, userName }) => {
  const {
    logos,
    name,
    clientKey,
    institutionKey,
    onboardingUrl,
    isActive,
    comingSoonHref,
    launchDate,
    topClientPlaceholder,
    highMiddleClientPlaceholder,
    lowMiddleClientPlaceholder,
    bottomClientPlaceholder,
    countries,
    showSwordLogo,
  } = client;

  usePersonalization(personalization);

  function extractFieldsFromTemplate() {
    if (!client.template.data) {
      return null;
    }

    return client.template.data.attributes;
  }

  const template = extractFieldsFromTemplate();
  // override template variables
  const header = {
    ...mergeObjects(template?.header, client?.header),
    logos: logos?.data,
    isActive,
    comingSoonHref,
  };

  const heroHeader = {
    ...mergeObjects(template?.heroHeader[0], client?.heroHeader[0]),
    ...{ content: mergeObjects(template?.heroHeader[0]?.content, client?.heroHeader[0]?.content) },
  };
  const topBanner = mergeObjects(template?.banner, client?.banner);
  const contact = mergeObjects(template?.contact, client?.contact);
  const disclaimer =
    client?.disclaimer ??
    client?.header?.disclaimer ??
    client?.template?.data?.attributes?.header?.disclaimer ??
    template?.disclaimer;

  const eligibility = getEligibilityClaim(client);

  if (heroHeader.content) {
    heroHeader.content.title = getValue('title', client);
    heroHeader.content.subtitle = getValue('subtitle', client);
    heroHeader.content.eligibilityClaim = eligibility;
  }

  const faqs = mergeObjects(template?.faqs, client?.faqs);
  const faqBanner = mergeObjects(template?.faqBanner, client?.faqBanner);
  const footnotes = client?.footnotes?.length ? client.footnotes : template?.footnotes;

  return (
    <>
      <ClientProvider
        defaultClient={{
          name,
          clientKey,
          institutionKey,
          onboardingUrl,
          isActive,
          launchDate,
          countries,
          comingSoonHref: header.comingSoonHref,
          product: products.THRIVE,
          country: client?.country,
        }}
      >
        {!referral && topBanner && (
          <TopBanner
            title={topBanner.title}
            phone={topBanner?.phoneNumberHref}
            label={topBanner.label}
            email={topBanner?.emailHref}
            active={topBanner?.isVisible}
          />
        )}
        <div>
          {!referral &&
            (heroHeader && heroHeader?.content ? (
              <DynamicHeader
                content={heroHeader}
                clientKey={clientKey}
                topBanner={!!topBanner && topBanner?.isVisible}
                logo={logos?.data}
                showSwordLogo={showSwordLogo}
                userName={userName}
              />
            ) : (
              <Header
                content={{ ...header, eligibilityClaim: eligibility }}
                countries={countries}
                showSwordLogo={showSwordLogo}
                userName={userName}
              />
            ))}
        </div>

        {topClientPlaceholder && (
          <DynamicBody body={topClientPlaceholder} client scrollTop={!!countries?.length} />
        )}
        {template?.topPlaceholder && (
          <DynamicBody body={template.topPlaceholder} client scrollTop={!!countries?.length} />
        )}
        {highMiddleClientPlaceholder && (
          <DynamicBody body={highMiddleClientPlaceholder} client scrollTop={!!countries?.length} />
        )}
        {template?.middlePlaceholder && (
          <DynamicBody body={template.middlePlaceholder} client scrollTop={!!countries?.length} />
        )}
        {lowMiddleClientPlaceholder && (
          <DynamicBody body={lowMiddleClientPlaceholder} client scrollTop={!!countries?.length} />
        )}
        {template?.bottomPlaceholder && (
          <DynamicBody body={template.bottomPlaceholder} client scrollTop={!!countries?.length} />
        )}
        {bottomClientPlaceholder && (
          <DynamicBody body={bottomClientPlaceholder} client scrollTop={!!countries?.length} />
        )}

        {template?.cta && (
          <HighlightedCTA
            content={template.cta}
            isActive={isActive}
            scrollTop={!!countries?.length}
          />
        )}
        {faqs && <Faqs content={faqs} />}
        {faqBanner && <FaqBanner content={faqBanner} contact={contact} />}
        {template?.app && <AppSupport content={template.app} />}
        {!referral && (
          <Footer
            bottomCTA={template?.bottomCTA}
            disclaimer={disclaimer}
            footnotes={footnotes}
            scrollTop={!!countries?.length}
            email={contact?.email}
          />
        )}
      </ClientProvider>
    </>
  );
};

export default ClientPage;
